'use client';

import { useRef, useState, useEffect, type ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';

// Gap between cells (px) — Tailwind `gap-3`. Used by the column math below.
const GRID_GAP = 12;

// Target tile edge (px) — the SINGLE constant shared with Apple/Android. The
// column count is derived from the measured container width / this target, so
// tiles stay the same physical size regardless of how many there are (a
// building with only two cards shows them at the SAME size as a busy one, never
// blown up). Yields ~2 columns on phones, ~3 on tablets, more on wide/desktop.
const TILE_TARGET = 185;

/**
 * Shared responsive grid for the Brief materials area. ONE adaptive
 * column/gap definition reused by every material card (Views, Docs, Sketches,
 * References) so cards are the same size for every type. The column count is
 * derived from the measured container width (min 2 columns). Mirrors
 * Apple/Android.
 */
export function BriefTileGrid({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      setWidth(entries[0]?.contentRect.width ?? 0);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Phones stay 2-up, but anything tablet-width (≥440px, incl. a tablet in
  // PORTRAIT where the materials column is narrower) shows at least 3.
  const minColumns = width >= 440 ? 3 : 2;
  const columns =
    width > 0
      ? Math.max(
          minColumns,
          Math.floor((width + GRID_GAP) / (TILE_TARGET + GRID_GAP))
        )
      : 2;

  return (
    <div
      ref={ref}
      className="grid gap-3"
      style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
    >
      {children}
    </div>
  );
}

interface BriefTileProps {
  /** Tile body (image / icon). Fills a square frame. */
  children: ReactNode;
  /** Small type caption under the card ("View" / "Doc" / "Sketch"). */
  caption: string;
  ariaLabel: string;
  /** Tap handler. Omit for non-interactive tiles (e.g. upload placeholders). */
  onClick?: () => void;
  /** Reduces opacity while a delete is in flight. */
  isDeleting?: boolean;
  /** Spread DnD sortable props onto the tile (Views only). */
  dndProps?: Record<string, unknown>;
  className?: string;
  style?: React.CSSProperties;
  buttonRef?: (node: HTMLElement | null) => void;
  /**
   * Optional "paper tilt" rotation in degrees (References only). Unified paper-card
   * look across web/Android/Apple: alternating ±2.5° + a soft paper shadow +
   * scale compensation so the rotated bounding box stays inside the grid cell
   * (matches Apple's paperCardStyleCompensated). Merged into the inline transform.
   */
  rotation?: number;
}

// Scale-down so a rotated square's bounding box fits its cell:
// box = s·(cos θ + sin θ); scale = 1/(cos θ + sin θ). Matches Apple at ±2.5°.
function paperScale(rotationDeg: number): number {
  const theta = (Math.abs(rotationDeg) * Math.PI) / 180;
  return 1 / (Math.cos(theta) + Math.sin(theta));
}

/**
 * Canonical square card + type caption for the unified Brief materials area.
 * Every material type (View, Doc, Sketch) renders through this single component
 * so chrome (square frame, border, soft shadow) and sizing are identical. The
 * square frame uses the Views/angles tile sizing as the single source of truth.
 */
export function BriefTile({
  children,
  caption,
  ariaLabel,
  onClick,
  isDeleting = false,
  dndProps,
  className,
  style,
  buttonRef,
  rotation,
}: BriefTileProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <button
        ref={buttonRef}
        style={
          rotation
            ? {
                ...style,
                transform: `rotate(${rotation}deg) scale(${paperScale(rotation)})`,
                // Soft "paper card on table" shadow — matches Apple (black 15%, r3, y2).
                boxShadow: '1px 2px 3px rgba(0,0,0,0.15)',
              }
            : style
        }
        type="button"
        {...dndProps}
        onClick={onClick}
        disabled={!onClick}
        aria-label={ariaLabel}
        className={cn(
          'relative aspect-square w-full rounded-lg overflow-hidden border border-border bg-muted shadow-sm',
          'text-start transition-shadow',
          onClick
            ? 'cursor-pointer hover:shadow-md'
            : 'cursor-default',
          isDeleting && 'opacity-50',
          className
        )}
      >
        {children}
      </button>
      <span className="text-card-meta text-muted-foreground/80 text-center">
        {caption}
      </span>
    </div>
  );
}
